<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex align-items-center">
              <p class="mb-0 text-uppercase font-weight-bold">Registrar Sucursal / Agencia</p>
            </div>
          </div>
          <div class="card-body">
            <form @submit.prevent="submitBranch">
              <h6 class="text-uppercase text-body text-xs font-weight-bolder">Información General</h6>
              <div class="row">
                <div class="col-md-8">
                  <label for="name" class="form-control-label">Nombre de la Sucursal</label>
                  <argon-input
                    id="name"
                    type="text"
                    v-model="branch.name"
                    placeholder="Ej. Agencia Central o Sucursal Norte"
                  />
                  <small v-if="submitted && v$.branch.name.$error" class="text-danger">El nombre es requerido</small>
                </div>

                <div class="col-md-4">
                  <label for="phone" class="form-control-label">Teléfono de Contacto</label>
                  <argon-input
                    id="phone"
                    type="text"
                    v-model="branch.phone"
                    placeholder="2222-2222"
                  />
                </div>

                <div class="col-md-12 mt-3">
                  <label for="address" class="form-control-label">Dirección Física</label>
                  <argon-textarea
                    id="address"
                    v-model="branch.address"
                    placeholder="Dirección exacta de la sucursal..."
                  />
                  <small v-if="submitted && v$.branch.address.$error" class="text-danger">La dirección es requerida</small>
                </div>
              </div>

              <hr class="horizontal dark mt-4 mb-3" />

              <h6 class="text-uppercase text-body text-xs font-weight-bolder">Geolocalización</h6>
              <div class="row">
                <div class="col-md-12 mt-2">
                  <label class="form-label text-sm text-muted">Haga clic en el mapa o arrastre el marcador para definir la ubicación</label>
                  <div id="branch-map" class="border-radius-lg shadow-sm mb-2" style="height: 350px; z-index: 1;"></div>

                  <div class="row">
                    <div class="col-6">
                      <label class="text-xs">Latitud</label>
                      <argon-input v-model="branch.latitude" readonly size="sm" />
                    </div>
                    <div class="col-6">
                      <label class="text-xs">Longitud</label>
                      <argon-input v-model="branch.longitude" readonly size="sm" />
                    </div>
                  </div>
                </div>
              </div>

              <argon-button
                type="submit"
                class="mt-4"
                color="success"
                fullWidth
                size="lg"
                :disabled="isLoadingSubmit"
              >
                <span v-if="isLoadingSubmit">Guardando...</span>
                <span v-else>Guardar Sucursal</span>
              </argon-button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonTextarea from "@/components/ArgonTextarea.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import axios from "axios";
import Swal from 'sweetalert2';
import L from 'leaflet';

const DEFAULT_CENTER = [12.132, -86.250]; // Coordenadas de ejemplo (Managua)

export default {
  name: "CreateBranch",
  components: {
    ArgonInput,
    ArgonButton,
    ArgonTextarea,
  },
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  data() {
    const companyId = parseInt(localStorage.getItem('companyId'));
    return {
      map: null,
      marker: null,
      submitted: false,
      isLoadingSubmit: false,
      branch: {
        company: companyId || null,
        name: null,
        address: null,
        latitude: DEFAULT_CENTER[0],
        longitude: DEFAULT_CENTER[1],
        phone: null,
      },
    };
  },
  validations() {
    return {
      branch: {
        name: { required },
        address: { required },
        company: { required },
      },
    };
  },
  methods: {
    initializeMap() {
      this.map = L.map('branch-map').setView(DEFAULT_CENTER, 13);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap'
      }).addTo(this.map);

      // Fix para iconos en entornos Webpack/Vite
      const icon = L.icon({
        iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
        iconUrl: require('leaflet/dist/images/marker-icon.png'),
        shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
        iconSize: [25, 41],
        iconAnchor: [12, 41]
      });

      this.marker = L.marker(DEFAULT_CENTER, { draggable: true, icon }).addTo(this.map);

      // Sincronizar coordenadas al arrastrar
      this.marker.on('dragend', () => {
        const position = this.marker.getLatLng();
        this.branch.latitude = position.lat.toFixed(8);
        this.branch.longitude = position.lng.toFixed(8);
      });

      // Sincronizar coordenadas al hacer click en el mapa
      this.map.on('click', (e) => {
        this.marker.setLatLng(e.latlng);
        this.branch.latitude = e.latlng.lat.toFixed(8);
        this.branch.longitude = e.latlng.lng.toFixed(8);
      });
    },

    async submitBranch() {
      this.submitted = true;
      this.v$.$touch();

      if (this.v$.$invalid) {
        Swal.fire({
          title: 'Campos requeridos',
          text: 'Por favor complete el nombre y la dirección de la sucursal.',
          icon: 'warning',
          confirmButtonColor: '#2dce89'
        });
        return;
      }

      this.isLoadingSubmit = true;
      const token = localStorage.getItem("accessToken");
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      };

      try {
        await axios.post(this.$store.state.apiURL + 'branches', this.branch, config);

        Swal.fire({
          title: 'Éxito',
          text: 'La sucursal ha sido creada correctamente.',
          icon: 'success',
          confirmButtonColor: '#2dce89',
        }).then(() => {
          this.$router.push('/branches'); // Ajusta según tu ruta de listado
        });
      } catch (err) {
        console.error('Error al crear sucursal:', err);
        Swal.fire('Error', err.response?.data?.error || 'No se pudo guardar la sucursal.', 'error');
      } finally {
        this.isLoadingSubmit = false;
      }
    }
  },
  mounted() {
    this.initializeMap();
  }
};
</script>

<style scoped>
/* Asegura que el mapa se vea correctamente */
#branch-map {
  border: 1px solid #dee2e6;
}
</style>